// The painted avatar: aligned storybook layers (scripts/avatar-gen.mjs) stacked on a canvas.
// Every layer is a full 1024 x 1536 frame, so stacking needs no offsets. Skin and hair are
// light gray and tinted to her chosen colors. Falls back to the SVG portrait when art is missing.

import { art, drawTinted, hasArt } from '@kit/assets';
import type { AvatarData } from './model';

export const PW = 1024, PH = 1536;

const id = (s: string) => `avatar.p.${s}`;

/** the layers this avatar needs, in drawing order */
function layers(a: AvatarData): { key: string; tint?: string }[] {
  const out: { key: string; tint?: string }[] = [];
  // capes and wings hang behind her; a backpack seen from the front is its straps, so it goes on top
  const backBehind = a.back && a.back !== 'back.backpack';
  if (backBehind) out.push({ key: id(`item.${a.back}`) });
  out.push({ key: id('base') }, { key: id('skin'), tint: a.body.skin });
  out.push({ key: id(`item.${a.bottom}`) }, { key: id(`item.${a.shoes}`) }, { key: id(`item.${a.top}`) });
  if (a.back === 'back.backpack') out.push({ key: id(`item.${a.back}`) });
  if (a.accessory === 'acc.scarf.teal') out.push({ key: id(`item.${a.accessory}`) });
  out.push({ key: id(`eyes.${a.eyes}`) }, { key: id(`mouth.${a.mouth}`) });
  out.push({ key: id(`hair.${a.hair.style}`), tint: a.hair.color });
  if (a.accessory && a.accessory !== 'acc.scarf.teal') out.push({ key: id(`item.${a.accessory}`) });
  if (a.hat) out.push({ key: id(`item.${a.hat}`) });
  return out;
}

/** painted only when every layer this outfit needs has loaded */
export function canPaint(a: AvatarData): boolean {
  return layers(a).every(l => hasArt(l.key));
}

const cache = new Map<string, HTMLCanvasElement>();
const MAX = 40;

/** a canvas of the painted avatar at the given width (height is 1.5x) */
export function paintedCanvas(a: AvatarData, width: number): HTMLCanvasElement | null {
  if (!canPaint(a)) return null;
  const w = Math.max(24, Math.round(width));
  const k = `${w}|${JSON.stringify(a)}`;
  const hit = cache.get(k);
  if (hit) return hit;
  const c = document.createElement('canvas');
  c.width = w; c.height = Math.round(w * PH / PW);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  for (const l of layers(a)) {
    if (l.tint) drawTinted(ctx, l.key, l.tint, 0, 0, c.width, c.height);
    else { const img = art(l.key); if (img) ctx.drawImage(img, 0, 0, c.width, c.height); }
  }
  cache.set(k, c);
  if (cache.size > MAX) cache.delete(cache.keys().next().value!);
  return c;
}

/** where her feet are in the painted frame (fraction of height), for standing her on the ground */
export const PAINTED_FEET = 0.975;
/** the painted figure's top of head (fraction of height), to crop thumbnails */
export const PAINTED_HEAD = 0.05;
